// ui/CreamTable.jsx: restyle step 2, the DOM parts of the cream table.
//
// The canvas (canvas/cream-board.js) draws the felt and the cards; these
// components draw everything with words, placed from the same layout
// (canvas/cream-layout.js): the score blocks and status row in the top
// bar, the seat tags, the names in empty trick cells, the controls row
// (last trick, the hint line, the bid buttons, the dealer's discard),
// the WIN badge and the turn timer.

import { h, Fragment } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { SUIT_MAPS, SUIT_INK } from '../canvas/pixel-cards.js';
import { renderCard, describeCard } from '../canvas/cards.js';
import { prepareHiDpiCanvas, readDevicePixelRatio, backingSize, keepStageAligned } from '../canvas/hidpi.js';
import { textWidth, shortName, trumpAnnounceBox, BID_WORD } from '../canvas/cream-layout.js';
import { computeBidPercent } from '../components/bid-timer-math.js';
import { buildButtons, dealerContextLine, GO_ALONE_CONFIRMATION_TEXT } from './bidding-logic.js';
import { stuckDealerCaption } from './stuck-dealer-caption.js';
import { SUIT_WORD } from '../views/cream-table-model.js';

/**
 * A suit in the cards' own pixel shape (9 x 9 cells of `px`).
 *
 * @param {{ suit: string, px?: number, color?: string|null }} props
 */
export function SuitIcon({ suit, px = 2, color = null }) {
  const map = SUIT_MAPS[suit];
  if (!map) return null;
  const rects = [];
  map.forEach((row, y) => [...row].forEach((ch, x) => {
    if (ch === 'X') rects.push(h('rect', { key: `${x},${y}`, x, y, width: 1, height: 1 }));
  }));
  return h('svg', {
    class: 'suit-icon', width: 9 * px, height: 9 * px, viewBox: '0 0 9 9',
    'shape-rendering': 'crispEdges', 'aria-hidden': 'true', fill: color ?? SUIT_INK[suit],
  }, rects);
}

/** Five trick marks, `px` tall, filled for tricks won (mockup marksSvg). */
function Marks({ won, px, gap }) {
  const step = px + gap;
  const w = 5 * step - gap;
  return h('svg', { width: w, height: px, viewBox: `0 0 ${w} ${px}`, 'shape-rendering': 'crispEdges', 'aria-hidden': 'true' },
    [0, 1, 2, 3, 4].map((i) => h('rect', { key: i, x: i * step, y: 0, width: px, height: px, class: i < won ? 'pip-on' : 'pip-off' })));
}

/**
 * One team's score block (C4): "3/7", the team tab, five trick marks and
 * TRICKS; "+1 TRICK" shows in place of the tab after a won trick.
 */
export function ScoreBlock({ label, score, target, tricks, mode, pop = false, tight = false, small = false, sheet = false }) {
  // tricks === null (the game-over card): no marks, only the total. sheet:
  // inside the recap or game-over card, where the block is not a top-bar block
  // (it has no .score-block class, so the table's own checks never see it).
  // tight (fit-a0): a 1 px gap between the phone marks; small (fit-a4,
  // the smallest landscape screens): phone-size marks.
  const px = small ? 10 : mode === 'desk' ? 14 : mode === 'land' ? 12 : 10;
  const markGap = mode === 'phone' || small ? (tight ? 1 : 2) : 3;
  return (
    <div
      class={`${sheet ? 'sheet-score' : 'score-block'} team-${label}${pop ? ' plus-on' : ''}`}
      data-team={sheet ? undefined : label}
      data-sheet-team={sheet ? label : undefined}
      data-testid={sheet ? undefined : `score-${label.toLowerCase()}`}
    >
      <span class="sb-num"><span class="sb-val">{score}</span><span class="sb-max">/{target}</span></span>
      <span class="sb-side">
        <span class="sb-row"><span class="sb-name">{label}</span></span>
        {tricks != null && (
          <span class="sb-marks">
            <span class="sb-tricks" role="img" aria-label={`${tricks} tricks this hand`}><Marks won={tricks} px={px} gap={markGap} /></span>
            <span class="sb-label">TRICKS</span>
          </span>
        )}
        {/* fit-a3 (the TRICKS word gone) leaves the side too narrow for "+1 TRICK": the word goes with it. */}
        <span class="sb-plus" data-testid="trick-pop" hidden={!pop}>+1<span class="sb-plus-word"> TRICK</span></span>
      </span>
    </div>
  );
}

/** Milliseconds left before the server's deadline (clock-offset corrected, never below zero). */
const msLeft = (deadlineMs, clockOffsetMs) => Math.max(0, deadlineMs - (Date.now() + clockOffsetMs));
/** Milliseconds until the whole seconds left (rounded up) next change. */
const toNextSecond = (remaining) => (remaining % 1000 || 1000) + 5;

/**
 * A value read from the time left before a deadline (null: no deadline),
 * kept current by a timer that fires only when the value changes. The
 * render that brings a new deadline reads the value at once, so it never
 * lags the deadline by a frame (the top bar is fitted in that render).
 *
 * @template T
 * @param {number|null} deadlineMs
 * @param {number} clockOffsetMs
 * @param {(remaining: number|null) => T} valueAt
 * @param {(remaining: number, value: T) => number|null} nextChangeIn  ms until the value changes, or null
 * @returns {T}
 */
function useCountdown(deadlineMs, clockOffsetMs, valueAt, nextChangeIn) {
  const read = () => valueAt(deadlineMs == null ? null : msLeft(deadlineMs, clockOffsetMs));
  const [state, setState] = useState(() => ({ dl: deadlineMs, off: clockOffsetMs, v: read() }));
  const fresh = state.dl === deadlineMs && state.off === clockOffsetMs;
  useEffect(() => {
    let timer;
    const tick = () => {
      const v = read();
      setState((st) => (st.dl === deadlineMs && st.off === clockOffsetMs && st.v === v ? st : { dl: deadlineMs, off: clockOffsetMs, v }));
      const wait = deadlineMs == null ? null : nextChangeIn(msLeft(deadlineMs, clockOffsetMs), v);
      if (wait != null) timer = setTimeout(tick, wait);
    };
    tick();
    return () => clearTimeout(timer);
  }, [deadlineMs, clockOffsetMs]);
  return fresh ? state.v : read();
}

const secondsAt = (remaining) => (remaining == null ? 0 : Math.ceil(remaining / 1000));
const nextSecond = (remaining) => (remaining > 0 ? toNextSecond(remaining) : null);
const digitsAt = (remaining) => (remaining == null ? 0 : String(Math.ceil(remaining / 1000)).length);
// One digit fewer once the seconds drop to 9, 99, ...
const nextDigitDrop = (remaining, digits) => (digits > 1 ? remaining - (10 ** (digits - 1) - 1) * 1000 + 5 : null);

/**
 * Whole seconds left on the current turn (rounded up), updated once a
 * second: only the component that shows them re-renders.
 *
 * @param {number} deadlineMs
 * @param {number} clockOffsetMs
 */
export function useTurnSeconds(deadlineMs, clockOffsetMs) {
  return useCountdown(deadlineMs, clockOffsetMs, secondsAt, nextSecond);
}

/**
 * How many digits the turn's seconds have (0 with no deadline). The top
 * bar is fitted to it, so it changes only when the digit count does
 * (100 to 99, 10 to 9), not every second.
 *
 * @param {number|null} deadlineMs
 * @param {number} clockOffsetMs
 */
export function useTurnDigits(deadlineMs, clockOffsetMs) {
  return useCountdown(deadlineMs, clockOffsetMs, digitsAt, nextDigitDrop);
}

/**
 * The turn chip's seconds (C8). It re-renders itself once a second; the
 * game view does not re-render for it.
 *
 * @param {{ deadlineMs: number, clockOffsetMs: number }} props
 */
export function TurnSeconds({ deadlineMs, clockOffsetMs }) {
  const secs = useTurnSeconds(deadlineMs, clockOffsetMs);
  return (
    <span class="turn-timer-ring" data-testid="turn-timer-ring" role="timer" aria-label={`${secs} seconds to act`}>
      {' '}<span class="turn-timer-ring__secs">{secs}</span>
    </span>
  );
}

/**
 * The active seat tag's draining bar (C8): a component of its own, so
 * only it re-renders as the bar drains (every frame; once a second
 * under reduced motion), never the game view. It sets only its own
 * transform (data-paint-only: no layout, and canvas/hidpi.js's stage
 * check ignores it), so nothing reads the page's boxes per frame.
 *
 * @param {{ deadlineMs: number, durationMs?: number|null, clockOffsetMs: number, reduced: boolean }} props
 */
export function TurnBar({ deadlineMs, durationMs, clockOffsetMs, reduced }) {
  // The turn's length: the server's, or (without one) what is left when the bar first shows.
  const total = useMemo(
    () => (durationMs != null && durationMs > 0 ? durationMs : msLeft(deadlineMs, clockOffsetMs)),
    [deadlineMs, durationMs, clockOffsetMs],
  );
  const shareNow = () => {
    const now = Date.now() + clockOffsetMs;
    return computeBidPercent({ deadlineMs, durationMs: total, nowMs: now }) / 100;
  };
  const [share, setShare] = useState(shareNow);
  useEffect(() => {
    let raf = 0;
    let timer;
    const tick = () => {
      const remaining = msLeft(deadlineMs, clockOffsetMs);
      setShare(shareNow());
      if (remaining <= 0) return;
      if (reduced) timer = setTimeout(tick, toNextSecond(remaining));
      else raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [deadlineMs, clockOffsetMs, total, reduced]);
  return <span class="tag-bar" data-testid="turn-bar" data-paint-only="" style={{ transform: `scaleX(${share})` }} />;
}

/**
 * A seat tag (C3): name rows, PARTNER, CALLED / ALONE / SITS OUT, cards
 * left, the turn highlight and (C8) the draining turn bar (`timer`:
 * the turn's deadline, for TurnBar; null with no timer running).
 */
export function SeatTag({ slot, rect, content, active, team, trumpSuit, timer }) {
  return (
    <div
      class={`seat-tag tag-${slot} team-${team}${active ? ' active' : ''}${rect.dir === 'col' ? ' col' : ''}${rect.tight ? ' tight' : ''}`}
      data-slot={slot}
      data-seat={content.seat}
      data-testid={`seat-tag-${slot}`}
      title={content.fullName}
      aria-label={`${content.fullName}${content.partner ? ', partner' : ''}${content.status ? `, ${content.status.toLowerCase()}` : ''}${content.count != null ? `, ${content.count} cards` : ''}${active ? ', on turn' : ''}`}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, '--tag-font': `${rect.font}px`, '--tag-line': `${rect.line}px` }}
    >
      {rect.rows.map((row, i) => (
        <div class="tag-row" key={i} aria-hidden="true" style={(rect.rowLines?.[i] ?? 1) > 1 ? { height: `${rect.rowLines[i] * rect.line + (rect.rowLines[i] - 1) * rect.rowGap}px` } : undefined}>
          {row.map((it, j) => {
            if (it.kind === 'name') return <span key={j} class="tag-name">{it.text}</span>;
            if (it.kind === 'partner') return <span key={j} class="tag-role">PARTNER</span>;
            if (it.kind === 'status') {
              // A two-word badge too wide for a side tag takes two lines.
              return (
                <span key={j} class={`tag-status${it.lines ? ' multi' : ''}`} data-testid="tag-status">
                  {it.lines ? it.lines.map((ln, k) => <Fragment key={k}>{k ? ' ' : ''}<span>{ln}</span></Fragment>) : it.text}
                  {trumpSuit && it.text !== 'SITS OUT' && rect.font > 16 ? <SuitIcon suit={trumpSuit} px={2} color="#ff8fa3" /> : null}
                </span>
              );
            }
            return <span key={j} class="tag-count"><span class="mini-back" aria-hidden="true" /><span class="tag-num">{it.text}</span></span>;
          })}
        </div>
      ))}
      {active && timer && <TurnBar {...timer} />}
    </div>
  );
}

/** Mini cards in the last-trick view (mockup XXS). */
const MINI = { w: 32, h: 44 };

/**
 * A 32 x 44 card (mockup XXS) on its own small canvas, for the last-trick
 * view. Like the board (canvas/hidpi.js): a backing of round(CSS size x
 * ratio) that follows ratio changes, and the card kept on a whole device
 * pixel wherever the row puts it.
 */
export function MiniCard({ cardId, winner = false, team = 'US', dpr = readDevicePixelRatio() }) {
  const ref = useRef(/** @type {HTMLCanvasElement|null} */ (null));
  const boxRef = useRef(/** @type {HTMLSpanElement|null} */ (null));
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ctx = prepareHiDpiCanvas(ref.current, MINI.w, MINI.h, dpr);
    if (!ctx) return;
    ctx.clearRect(0, 0, MINI.w, MINI.h);
    renderCard(ctx, cardId, 0, 0, MINI.w, MINI.h, { face_up: true });
  }, [cardId, dpr]);
  useLayoutEffect(() => (boxRef.current ? keepStageAligned(boxRef.current, dpr) : undefined), [dpr]);
  const backing = backingSize(MINI.w, MINI.h, dpr);
  return (
    <span ref={boxRef} class={`mini-card${winner ? ` winner-small win-${team}` : ''}`} role="img" aria-label={describeCard(cardId)} data-winner={winner ? 'true' : undefined}>
      <canvas ref={ref} width={backing.width} height={backing.height} style={{ width: `${MINI.w}px`, height: `${MINI.h}px`, display: 'block' }} />
    </span>
  );
}

/**
 * What the last-trick view shows in a controls row `rowW` px wide: the
 * X button, the leader and winner words when they fit, and the cards
 * (gaps tighten on the narrowest rows). The winner's dashed outline sits
 * inside its card, so the view is never taller than the 44 px row.
 *
 * @param {number} rowW
 * @param {number} cards  cards in the last trick (3 when someone sat out)
 * @param {number} wordsW the wider of the two word lines
 */
export function peekFit(rowW, cards, wordsW) {
  for (const [gap, cardGap] of [[8, 4], [4, 2]]) {
    const cardsW = cards ? cards * MINI.w + (cards - 1) * cardGap : 0;
    const base = 44 + gap + cardsW;
    if (base > rowW) continue;
    return { gap, cardGap, showWords: base + wordsW + gap <= rowW, width: base };
  }
  return null;
}

/**
 * The controls row in play (C6): LAST TRICK, which opens the last trick
 * inside the row, and the hint line (and "Sending…" while a move is out).
 */
export function PlayControls({ rect, hint, hintTestId, sending = false, last, names, myTeam, showLastTrick, dpr = readDevicePixelRatio() }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!showLastTrick) setOpen(false); }, [showLastTrick]);
  const led = last ? `${shortName(names[last.leader] ?? '').toUpperCase()} LED` : 'NO TRICK';
  const won = last ? `${shortName(names[last.winner] ?? '').toUpperCase()} WON` : 'YET';
  const wordsW = Math.max(textWidth(led, 16), textWidth(won, 16));
  const fit = peekFit(rect.w, last ? last.plays.length : 0, wordsW);
  useEffect(() => {
    if (!fit) console.error('[cream-table] the last-trick view does not fit its controls row', { width: rect.w });
  }, [!fit, rect.w]);
  const winTeam = last ? (((last.winner === 1 || last.winner === 3) ? 'team_a' : 'team_b') === myTeam ? 'US' : 'THEM') : 'US';
  return (
    <div class={`controls-row${open ? ' peek-open' : ''}`} data-testid="controls-row" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, gap: open && fit ? `${fit.gap}px` : undefined }}>
      {showLastTrick && (
        <button
          type="button"
          class="btn peek-button"
          data-testid="last-trick"
          aria-expanded={open ? 'true' : 'false'}
          aria-label={open ? 'Close last trick' : 'Show last trick'}
          onClick={() => setOpen(!open)}
        >
          {open ? 'X' : 'LAST TRICK'}
        </button>
      )}
      {open ? (
        <div class="peek" data-testid="peek" style={fit ? { gap: `${fit.gap}px` } : undefined} aria-label={last ? `Last trick: ${led.toLowerCase()}, ${won.toLowerCase()}` : 'No trick yet'}>
          {(!last || fit?.showWords) && <span class="peek-words"><span>{led}</span><span>{won}</span></span>}
          {last && <span class="peek-cards" style={fit ? { gap: `${fit.cardGap}px` } : undefined}>{last.plays.map((p) => <MiniCard key={p.card_id} cardId={p.card_id} winner={p.seat === last.winner} team={winTeam} dpr={dpr} />)}</span>}
        </div>
      ) : (
        <span class={`hint${sending ? ' msg' : ''}`} data-testid={hintTestId || 'hint-line'} role="status" aria-live="polite">
          {sending ? <span data-testid="command-sending">{hint}</span> : hint}
        </span>
      )}
    </div>
  );
}

/**
 * The bid buttons and the dealer line over the controls row (C9), at
 * the boxes the layout gave them (cream-layout.js arrangeBid): every
 * button at least 44 x 44; Go alone asks first.
 *
 * @param {{ L: import('../canvas/cream-layout.js').CreamLayout, state: any, seat: number,
 *   onBid: (bid: any) => void, seatLabels?: Record<number, string>, disabledReason?: string|null,
 *   sending?: boolean }} props
 */
export function CreamBidControls({ L, state, seat, onBid, seatLabels, disabledReason = null, sending = false }) {
  const buttons = orderedBidButtons(state, seat);
  const dealerLine = bidLineText(state, seat, seatLabels);
  const [pending, setPending] = useState(/** @type {any} */ (null));
  const dialogRef = useRef(/** @type {HTMLDialogElement|null} */ (null));
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (pending) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
      const confirm = d.querySelector('button[data-action="confirm-alone"]');
      if (confirm) /** @type {HTMLElement} */ (confirm).focus();
    } else if (typeof d.close === 'function') d.close();
    else d.removeAttribute('open');
  }, [pending]);
  const box = L.bid;
  if (buttons.length === 0 || !box || box.buttons.length !== buttons.length) return null;
  const click = (btn) => {
    if (disabledReason) return;
    if (btn.role === 'go_alone') { setPending(btn); return; }
    onBid(btn.bid);
  };
  const local = (r) => ({ left: r.x - box.x, top: r.y - box.y, width: r.w, height: r.h });
  const line = box.line;
  return (
    <>
      <div
        class="bid-controls"
        role="toolbar"
        aria-label="Bidding controls"
        style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
      >
        {(disabledReason || dealerLine) ? (
          <p
            class={line ? `bid-line bid-line-${line.place}` : 'bid-line sr-only'}
            data-testid={disabledReason ? 'bidding-disabled-reason' : 'bidding-dealer-context'}
            role={disabledReason ? 'status' : undefined}
            style={line ? local(line) : undefined}
          >
            {disabledReason
              ? (sending ? <span data-testid="command-sending">{disabledReason}</span> : disabledReason)
              : line
                ? line.lines.map((ln, k) => <Fragment key={k}>{k ? ' ' : ''}<span class="bid-line-text">{caseOf(dealerLine, ln)}</span></Fragment>)
                : dealerLine}
          </p>
        ) : null}
        {buttons.map((btn, i) => {
          const r = box.buttons[i];
          const word = BID_WORD[btn.role] ?? '';
          const words = word.split(' ');
          return (
            <button
              key={btn.key}
              type="button"
              class={`btn bid-btn bid-${btn.role} face-${r.face}`}
              data-bid-role={btn.role}
              data-bid-suit={btn.suit || ''}
              aria-label={btn.ariaLabel}
              disabled={!!disabledReason}
              aria-disabled={!!disabledReason}
              onClick={() => click(btn)}
              style={local(r)}
            >
              {btn.role === 'go_alone' ? <span class="sr-only">Go </span> : null}
              {/* The words keep the old labels' text ("Pass", "Call ♠", "Go Alone ♠") for anything that reads it. */}
              <span class={r.face === 'icon' ? 'bid-word sr-only' : 'bid-word'}>
                {r.face === 'narrow' && words.length > 1
                  ? titleCase(word).split(' ').map((wd, k) => <Fragment key={k}>{k ? ' ' : ''}<span class="bid-word-line">{wd}</span></Fragment>)
                  : titleCase(word)}
              </span>
              {btn.suit && <span class="sr-only"> {{ S: '♠', H: '♥', D: '♦', C: '♣' }[btn.suit]}</span>}
              {btn.suit && r.face !== 'narrow' && <SuitIcon suit={btn.suit} px={2} />}
            </button>
          );
        })}
      </div>
      <dialog
        ref={dialogRef}
        class="cream-dialog"
        data-bid-modal="go-alone"
        aria-modal="true"
        aria-labelledby="alone-title"
        onClose={() => setPending(null)}
        onCancel={() => setPending(null)}
      >
        <h2 id="alone-title">Go alone?</h2>
        <p>{GO_ALONE_CONFIRMATION_TEXT}</p>
        <div class="dialog-buttons">
          <button type="button" class="btn" data-action="cancel-alone" onClick={() => setPending(null)}>Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            data-action="confirm-alone"
            onClick={() => { if (pending && !disabledReason) onBid(pending.bid); setPending(null); }}
          >
            Go alone
          </button>
        </div>
      </dialog>
    </>
  );
}

/** "ORDER UP" -> "Order up": the label's own case (the button shows it upper case). */
function titleCase(word) {
  const w = word.toLowerCase();
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** The piece of `text` that `upper` (one of its lines, upper case) shows, in `text`'s own case. */
function caseOf(text, upper) {
  const i = text.toUpperCase().indexOf(upper);
  return i >= 0 ? text.slice(i, i + upper.length) : upper;
}

/**
 * This turn's bid buttons in the toolbar's order: the calls (or Order up)
 * and Pass, then the Go alone buttons.
 *
 * @param {any} state
 * @param {number} seat
 */
export function orderedBidButtons(state, seat) {
  const buttons = buildButtons(state, seat);
  return [...buttons.filter((b) => b.role !== 'go_alone'), ...buttons.filter((b) => b.role === 'go_alone')];
}

/**
 * The dealer line over the bid buttons: the stuck dealer's caption, or
 * who deals and what the up-card does.
 *
 * @param {any} state
 * @param {number} seat
 * @param {Record<number, string>|undefined} seatLabels
 * @returns {string}
 */
export function bidLineText(state, seat, seatLabels) {
  return stuckDealerCaption(/** @type {any} */ (state), seat)
    ?? dealerContextLine(/** @type {any} */ (state), seatLabels, seat) ?? '';
}

/**
 * The dealer's discard in the controls row (C9): what is picked, and
 * DISCARD to confirm.
 */
/**
 * @param {{ rect: {x:number, y:number, w:number, h:number}, selectedCardId: string|null,
 *   onConfirm: () => void, disabledReason?: string|null, sending?: boolean }} props
 */
/**
 * The dealer's prompt in the controls row. `pickup` names the pickup
 * ("You pick up ♦") ahead of the instruction until a card is picked.
 *
 * @param {{ rect: {x:number,y:number,w:number,h:number}, selectedCardId: string|null, onConfirm: () => void,
 *   disabledReason?: string|null, sending?: boolean, pickup?: string }} props
 */
export function CreamDiscardPrompt({ rect, selectedCardId, onConfirm, disabledReason = null, sending = false, pickup = '' }) {
  const buttonRef = useRef(/** @type {HTMLButtonElement|null} */ (null));
  useEffect(() => {
    if (selectedCardId && buttonRef.current) buttonRef.current.focus();
  }, [selectedCardId]);
  const enabled = !!selectedCardId && !disabledReason;
  return (
    <div
      class="discard-prompt"
      data-testid="discard-prompt"
      role="region"
      aria-label="Dealer discard prompt"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
    >
      <div class="discard-text" id="discard-instruction" aria-live="polite" aria-atomic="true">
        {disabledReason
          ? <span data-testid="discard-disabled-reason">{sending ? <span data-testid="command-sending">{disabledReason}</span> : disabledReason}</span>
          : enabled
            ? <>Discard <strong data-testid="discard-selected">{describeCard(selectedCardId)}</strong>?</>
            : <>{pickup ? `${pickup}. ` : ''}Pick a card to discard</>}
      </div>
      <button
        ref={buttonRef}
        type="button"
        class="btn btn-primary"
        data-action="discard-confirm"
        disabled={!enabled}
        aria-disabled={!enabled}
        aria-describedby="discard-instruction"
        onClick={onConfirm}
      >
        Discard
      </button>
    </div>
  );
}

/** The WIN badge inside the winning trick card (C7). */
export function WinBadge({ rect, team, cardId }) {
  return (
    <span
      class={`win-badge team-${team}`}
      data-testid="win-badge"
      data-team={team}
      data-card={cardId}
      aria-hidden="true"
      style={{ left: rect.x + rect.w - 4 - 38, top: rect.y + (rect.w >= 100 ? 6 : 4) }}
    >
      WIN
    </span>
  );
}

/**
 * The trump announcement (the mockup's announce box): the suit, its name
 * and who called it, over the trick grid; centred over the up-card while
 * it sits in the dealer's cell (cream-layout.js trumpAnnounceBox).
 */
export function TrumpAnnounce({ event, L, avoidSlot, makerLine, holdMs, fadeMs, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (!event) { setVisible(false); return undefined; }
    const show = requestAnimationFrame(() => setVisible(true));
    const fade = setTimeout(() => setVisible(false), holdMs);
    const done = setTimeout(() => dismiss.current?.(), holdMs + fadeMs);
    return () => { cancelAnimationFrame(show); clearTimeout(fade); clearTimeout(done); };
  }, [event, holdMs, fadeMs]);
  const word = event ? SUIT_WORD[event.trump_suit] ?? '' : '';
  const maker = (makerLine || '').toUpperCase();
  const r = event ? trumpAnnounceBox(L, avoidSlot, word, maker) : null;
  // Named once per layout change (not on every render): the supported
  // fallback draws the announcement at its smallest.
  const misfit = r && !r.fits ? `${L.mode}:${L.grid.x},${L.grid.y},${L.grid.w},${L.grid.h}:${avoidSlot ?? ''}` : '';
  useEffect(() => {
    if (misfit) console.error('[trump splash] no room over the trick grid for the trump announcement; drawn at its smallest', { mode: L.mode, grid: L.grid });
  }, [misfit]);
  if (!event || !r) return null;
  return (
    <div
      class={`trump-announce${r.stack ? ' stack' : ''}`}
      data-testid="trump-splash"
      data-visible={visible ? 'true' : 'false'}
      data-suit={event.trump_suit}
      data-placement={r.placement}
      data-size={L.mode}
      data-maker={maker}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{ left: r.x, top: r.y, width: r.w, height: r.h, padding: `${r.pad}px`, borderWidth: r.border, gap: r.stack ? r.gap : 12,
        fontFamily: r.face === 'tiny5' ? 'var(--cream-body)' : undefined, fontWeight: r.face === 'tiny5' ? 400 : undefined }}
    >
      <span class="an-suit" data-testid="trump-splash-glyph" aria-hidden="true"><SuitIcon suit={event.trump_suit} px={r.iconPx} /></span>
      <span class="an-text" style={{ gap: r.gap }}>
        <span class="an-big" data-testid="trump-splash-title" style={{ fontSize: `${r.big}px`, lineHeight: `${Math.round(r.big * 1.25)}px` }}>{word}</span>
        {r.small ? <span class="an-small" style={{ fontSize: `${r.small}px`, lineHeight: `${Math.round(r.small * 1.25)}px` }}>ARE TRUMP</span> : <span class="sr-only">are trump</span>}
        <span class="an-maker" data-testid="trump-splash-maker">
          {r.makerLines.map((line, i) => <Fragment key={i}>{i ? ' ' : ''}<span>{line}</span></Fragment>)}
        </span>
      </span>
    </div>
  );
}

export { Fragment };
